import React, { useEffect, useMemo, useState } from 'react'

import { parseCustomFieldHtml } from 'utils'
import { isLessonTranscriptVisible, readWistiaTranscript } from 'services/course-details'

import Button from 'components/Form/Button'
import CertificateButton from 'components/Certificade'
import CourseDetailsResourcesCard from 'components/CourseDetails/ResourcesCard'

import {
  ActionHint,
  ApprovalStatus,
  ActionsStrip,
  BodyText,
  CategoryBadge,
  ContentEyebrow,
  LessonLabel,
  PanelMeta,
  PanelContent,
  PanelRoot,
  PanelTitle,
  TranscriptBody,
  TabButton,
  TabHeader,
  TranscriptText,
  SurveyAction,
} from './styled'

export default function CourseDetailsTabs({
  lesson,
  locale,
  title,
  subtitle,
  category,
  actionHint,
  courseType,
  hasQuiz = false,
  canStartTest,
  canGetCertificate,
  canOpenSurvey,
  handleStartQuiz,
  handleOpenSurvey,
  certificateData,
  certificateApproval,
  certificateFileName,
  transcriptLanguage,
  moduleResources,
  surveyActionLabel,
  surveyCompleted,
}) {
  const [activeTab, setActiveTab] = useState('overview')
  const [transcriptState, setTranscriptState] = useState({
    error: null,
    loading: false,
    text: '',
  })

  const showTranscript = isLessonTranscriptVisible(lesson)
  const transcriptKey = [
    courseType,
    lesson?.moduleId,
    lesson?.id,
    lesson?.video_id,
    transcriptLanguage || locale || 'default',
  ].join(':')

  const parsedContent = useMemo(
    () => parseCustomFieldHtml(lesson?.description || lesson?.content || ''),
    [lesson?.content, lesson?.description]
  )

  useEffect(() => {
    setActiveTab('overview')
    setTranscriptState({ error: null, loading: false, text: '' })
  }, [lesson?.id, lesson?.moduleId, showTranscript])

  useEffect(() => {
    if (activeTab !== 'transcript' || !showTranscript) return

    const videoId = String(lesson?.video_id || '').trim()
    if (!videoId) {
      setTranscriptState({
        error: null,
        loading: false,
        text: '',
      })
      return
    }

    let active = true

    setTranscriptState({
      error: null,
      loading: true,
      text: '',
    })

    readWistiaTranscript(videoId, {
      language: transcriptLanguage,
      locale,
      type: courseType,
      moduleId: lesson?.moduleId,
      lessonId: lesson?.id,
    })
      .then(result => {
        if (!active) return

        const data = result?.data || null
        const nextState = result?.error
          ? {
            error: result?.error?.message || 'Unable to load transcript.',
            loading: false,
            text: '',
          }
          : {
            error: null,
            loading: false,
            text: data?.available ? (data?.text || '') : '',
          }

        setTranscriptState(nextState)
      })
      .catch(() => {
        if (!active) return

        const nextState = {
          error: 'Unable to load transcript.',
          loading: false,
          text: '',
        }
        setTranscriptState(nextState)
      })

    return () => {
      active = false
    }
  }, [activeTab, courseType, lesson?.id, lesson?.moduleId, lesson?.video_id, locale, showTranscript, transcriptKey, transcriptLanguage])

  return (
    <PanelRoot>
      <TabHeader>
        <TabButton aria-selected={activeTab === 'overview'} active={activeTab === 'overview'} onClick={() => setActiveTab('overview')} role="tab" type="button">
          Overview
        </TabButton>
        <TabButton aria-selected={activeTab === 'resources'} active={activeTab === 'resources'} onClick={() => setActiveTab('resources')} role="tab" type="button">
          Resources
        </TabButton>
        {!showTranscript ? null : (
          <TabButton aria-selected={activeTab === 'transcript'} active={activeTab === 'transcript'} onClick={() => setActiveTab('transcript')} role="tab" type="button">
            Transcript
          </TabButton>
        )}
      </TabHeader>

      <PanelContent>
        {activeTab === 'overview' ? (
          <>
            {!category && !lesson?.title ? null : (
              <ContentEyebrow>
                {!category ? null : <CategoryBadge>{category}</CategoryBadge>}
                {!lesson?.title ? null : <LessonLabel>{lesson.title}</LessonLabel>}
              </ContentEyebrow>
            )}
            <PanelTitle>{title || lesson?.title}</PanelTitle>
            {!subtitle ? null : <PanelMeta>{subtitle}</PanelMeta>}
            {(lesson?.description || lesson?.content) ? (
              <BodyText dangerouslySetInnerHTML={parsedContent} />
            ) : (
              <BodyText>No overview available for this lesson yet.</BodyText>
            )}

            {courseType !== 'courses' ? null : (
              <>
                {!actionHint ? null : <ActionHint>{actionHint}</ActionHint>}
                {!certificateApproval ? null : (
                  <ApprovalStatus
                    approved={certificateApproval.status === 'approved'}
                    rejected={certificateApproval.status === 'rejected'}
                  >
                    Training approval: {certificateApproval.status === 'pending_trainer_approval'
                      ? 'pending trainer approval'
                      : certificateApproval.status}
                  </ApprovalStatus>
                )}
                {!canOpenSurvey && !hasQuiz && !(canGetCertificate && certificateData) ? null : (
                  <ActionsStrip>
                    {!canOpenSurvey ? null : (
                      <SurveyAction>
                        <Button
                          onClick={handleOpenSurvey}
                          width="100%"
                          color="blue"
                          outline={surveyCompleted}
                          nospace
                        >
                          {surveyActionLabel}
                        </Button>
                      </SurveyAction>
                    )}

                    {!hasQuiz ? null : (
                      <Button
                        onClick={handleStartQuiz}
                        width="240px"
                        color="blue"
                        nospace
                        disabled={!canStartTest}
                      >
                        Start the test
                      </Button>
                    )}

                    {!canGetCertificate || !certificateData ? null : (
                      <CertificateButton
                        buttonColor="blue"
                        buttonWidth="240px"
                        model={certificateData.model || 1}
                        fileName={certificateFileName}
                        data={certificateData}
                        label="Get your certificate"
                      />
                    )}
                  </ActionsStrip>
                )}
              </>
            )}
          </>
        ) : activeTab === 'resources' ? (
          <CourseDetailsResourcesCard
            embedded
            emptyText="No resources available for this module yet."
            resources={moduleResources}
            subtitle={lesson?.title ? 'Resources for the selected module' : null}
            title="Resources"
          />
        ) : showTranscript ? (
          <>
            <PanelTitle>Transcript</PanelTitle>
            <TranscriptBody>
              {transcriptState.loading ? (
                <BodyText>Loading transcript...</BodyText>
              ) : transcriptState.error ? (
                <BodyText>{transcriptState.error}</BodyText>
              ) : transcriptState.text ? (
                <TranscriptText>{transcriptState.text}</TranscriptText>
              ) : (
                <BodyText>No transcript available for this video yet.</BodyText>
              )}
            </TranscriptBody>
          </>
        ) : null}
      </PanelContent>
    </PanelRoot>
  )
}
